import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from 'react';

const DEFAULT_MAX_HISTORY = 50;
const DEFAULT_DEBOUNCE_MS = 600;

type UseFormDataHistoryOptions<T> = {
  formData: T;
  setFormData: Dispatch<SetStateAction<T>>;
  enabled?: boolean;
  maxHistory?: number;
  debounceMs?: number;
};

type UseFormDataHistoryResult = {
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  reset: () => void;
};

export const useFormDataHistory = <T>(
  options: UseFormDataHistoryOptions<T>,
): UseFormDataHistoryResult => {
  const {
    formData,
    setFormData,
    enabled = true,
    maxHistory = DEFAULT_MAX_HISTORY,
    debounceMs = DEFAULT_DEBOUNCE_MS,
  } = options;

  const stacksRef = useRef<{ past: string[]; future: string[] }>({
    past: [],
    future: [],
  });
  const lastSnapshotRef = useRef<string>(JSON.stringify(formData));
  const skipNextRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const [, forceUpdate] = useReducer((tick: number) => tick + 1, 0);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (skipNextRef.current) {
      skipNextRef.current = false;
      lastSnapshotRef.current = JSON.stringify(formData);
      return;
    }

    const snapshot = JSON.stringify(formData);

    if (snapshot === lastSnapshotRef.current) {
      return;
    }

    const previousSnapshot = lastSnapshotRef.current;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      stacksRef.current.past.push(previousSnapshot);
      if (stacksRef.current.past.length > maxHistory) {
        stacksRef.current.past.shift();
      }
      stacksRef.current.future = [];
      lastSnapshotRef.current = JSON.stringify(formDataRef.current);
      forceUpdate();
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [formData, enabled, maxHistory, debounceMs]);

  const undo = useCallback(() => {
    if (stacksRef.current.past.length === 0) {
      return;
    }

    const previous = stacksRef.current.past.pop();

    if (previous === undefined) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const current = JSON.stringify(formDataRef.current);
    stacksRef.current.future.push(current);
    lastSnapshotRef.current = previous;
    skipNextRef.current = true;
    setFormData(JSON.parse(previous));
    forceUpdate();
  }, [setFormData]);

  const redo = useCallback(() => {
    if (stacksRef.current.future.length === 0) {
      return;
    }

    const next = stacksRef.current.future.pop();

    if (next === undefined) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const current = JSON.stringify(formDataRef.current);
    stacksRef.current.past.push(current);
    lastSnapshotRef.current = next;
    skipNextRef.current = true;
    setFormData(JSON.parse(next));
    forceUpdate();
  }, [setFormData]);

  const reset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    stacksRef.current = { past: [], future: [] };
    lastSnapshotRef.current = JSON.stringify(formDataRef.current);
    skipNextRef.current = false;
    forceUpdate();
  }, []);

  return {
    canUndo: stacksRef.current.past.length > 0,
    canRedo: stacksRef.current.future.length > 0,
    undo,
    redo,
    reset,
  };
};
